import { KeyboardEvent, MouseEvent, TouchEvent } from "react";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { ContiguousTimeslot, Timeslot } from "@core/features/timeslots";
import { SchedulingAvailabilityTimeSlotBadge } from "../SchedulingAvailabilityTimeSlotBadge";

export type Props = {
  timeSlot: Timeslot;
  isSelected: boolean;
  isFirstSlotInContiguousBlock: boolean;
  matchingSlot?: ContiguousTimeslot;
  onMouseDown: (e: MouseEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onMouseOver: (e: MouseEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onTouchStart: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onTouchMove: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onTouchEnd: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  dayIndex: number;
  timeIndex: number;
  /** Whether this slot is part of a selectable option (for select-options/repeating mode) */
  isSelectableOption: boolean;
  /** Whether this slot is part of a currently selected option */
  isPartOfSelectedOption: boolean;
  /** Whether we're in an options-based mode (select-options or repeating) */
  isOptionsMode: boolean;
  /** The option ID this slot belongs to (for group hover in options mode) */
  optionId?: string;
  /** Callback to set hovered option ID for group hover effect */
  onOptionHover?: (optionId: string | null) => void;
  /** Set of currently hovered option IDs (for group hover effect, includes repeating groups) */
  hoveredOptionIds?: Set<string>;
  /** Callback to set focused option ID for group focus effect */
  onOptionFocus?: (optionId: string | null) => void;
  /** Set of currently focused option IDs (for group focus effect, includes repeating groups) */
  focusedOptionIds?: Set<string>;
  /** Whether we're in repeating mode (for left border styling) */
  isRepeatingMode?: boolean;
  /** Whether this is the last row in the grid (for bottom border) */
  isLastRow?: boolean;
  /** Whether this slot should be in the tab order (for keyboard navigation) */
  isTabbable?: boolean;
  /** Whether this is the first slot in its option (for focus border styling) */
  isFirstSlotOfOption?: boolean;
  /** Whether this is the last slot in its option (for focus border styling) */
  isLastSlotOfOption?: boolean;
};

export function SchedulingAvailabilityTimeSlot({
  timeSlot,
  isSelected,
  isFirstSlotInContiguousBlock,
  matchingSlot,
  onMouseDown,
  onMouseOver,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  dayIndex,
  timeIndex,
  isSelectableOption,
  isPartOfSelectedOption,
  isOptionsMode,
  optionId,
  onOptionHover,
  hoveredOptionIds,
  onOptionFocus,
  focusedOptionIds,
  isRepeatingMode,
  isLastRow,
  isTabbable = true,
  isFirstSlotOfOption,
  isLastSlotOfOption,
}: Props) {
  // In options mode, non-selectable slots are disabled
  const isDisabled = isOptionsMode && !isSelectableOption;
  // Check if this slot's option is currently hovered (for group hover effect)
  // In repeating mode, hoveredOptionIds will contain all options with the same time range
  const isOptionHovered = isOptionsMode && optionId && hoveredOptionIds?.has(optionId);
  // Check if this slot's option is currently focused (for group focus effect)
  const isOptionFocused = isOptionsMode && optionId && focusedOptionIds?.has(optionId);
  // Combined hover/focus state for styling
  const isOptionHighlighted = isOptionHovered || isOptionFocused;
  const onKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    timeSlot: Timeslot,
    dayIndex: number,
    timeIndex: number
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!isDisabled) {
        onMouseDown(e as unknown as MouseEvent<HTMLDivElement>, timeSlot);
      }
    }

    const getNext = (nextTimeIndex: number, nextDayIndex: number) => {
      const selector = `[data-time-index="${nextTimeIndex}"][data-day-index="${nextDayIndex}"]`;
      const nextEl = document.querySelector(selector) as HTMLElement | null;
      nextEl?.focus();
    };

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        getNext(timeIndex, dayIndex + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        getNext(timeIndex, dayIndex - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        getNext(timeIndex + 1, dayIndex);
        break;
      case "ArrowUp":
        e.preventDefault();
        getNext(timeIndex - 1, dayIndex);
        break;
    }
  };

  // Format the date for the aria-label
  const formattedDate = timeSlot.local.start.fullDate;
  const formattedTime = moment()
    .hour(timeSlot.local.start.hour)
    .minute(timeSlot.local.start.minute)
    .format("h:mm A");

  // Handle mouse enter/leave for group hover
  const handleMouseEnter = () => {
    if (isOptionsMode && optionId && onOptionHover) {
      onOptionHover(optionId);
    }
  };

  const handleMouseLeave = () => {
    if (isOptionsMode && onOptionHover) {
      onOptionHover(null);
    }
  };

  // Handle focus/blur for group focus effect
  const handleFocus = () => {
    if (isOptionsMode && optionId && onOptionFocus) {
      onOptionFocus(optionId);
    }
  };

  const handleBlur = () => {
    if (isOptionsMode && onOptionFocus) {
      onOptionFocus(null);
    }
  };

  return (
    <div
      className={twMerge(
        "time-slot",
        "touch-pan-x",
        "h-6 relative",
        "border-r border-t border-solid border-brand-300 transition-colors duration-100",
        // Add left border in repeating mode (since columns have gaps)
        isRepeatingMode && "border-l",
        // Cursor style based on disabled state
        isDisabled ? "cursor-default" : "cursor-pointer",
        // Selected states (freeform mode)
        !isOptionsMode &&
          isSelected &&
          matchingSlot?.duration &&
          matchingSlot.duration >= 2 &&
          "bg-brand-600 hover:bg-brand-500",
        !isOptionsMode &&
          isSelected &&
          matchingSlot?.duration &&
          matchingSlot.duration < 2 &&
          "bg-brand-400 hover:bg-brand-300",
        // Selectable option highlighting (for select-options/repeating mode)
        // Use isOptionHighlighted for group hover/focus effect instead of individual hover
        isOptionsMode && isSelectableOption && !isPartOfSelectedOption && !isOptionHighlighted && "bg-brand-100",
        isOptionsMode && isSelectableOption && !isPartOfSelectedOption && isOptionHighlighted && "bg-brand-200",
        isOptionsMode && isSelectableOption && isPartOfSelectedOption && !isOptionHighlighted && "bg-brand-600",
        isOptionsMode && isSelectableOption && isPartOfSelectedOption && isOptionHighlighted && "bg-brand-500",
        // Default hover state (freeform mode only)
        !isOptionsMode && !isSelected && "hover:bg-brand-100",
        // Disabled state (options mode, non-selectable slots) - no hover effect
        isDisabled && "bg-transparent",
        // Bottom border for last row
        isLastRow && "border-b",
        // Group focus border for options mode - show border around entire option block
        isOptionsMode && isOptionFocused && "border-brand-600 z-10",
        isOptionsMode && isOptionFocused && isFirstSlotOfOption && "border-t-2",
        isOptionsMode && isOptionFocused && isLastSlotOfOption && "border-b-2",
        isOptionsMode && isOptionFocused && "border-l-2 border-r-2",
        // Focus state (only for non-disabled, freeform mode)
        !isOptionsMode && !isDisabled && "focus:outline-none focus:z-20 focus:border focus:ring-2 focus:ring-offset-0 focus:ring-brand-600",
        // Remove default focus ring in options mode (we use group border instead)
        isOptionsMode && "focus:outline-none"
      )}
      style={{
        borderTopStyle: timeSlot.local.start.minute === 0 ? "solid" : "dashed",
      }}
      tabIndex={isDisabled || !isTabbable ? -1 : 0}
      role="gridcell"
      aria-label={`Time slot: ${formattedDate}, ${formattedTime}. ${isPartOfSelectedOption ? "Selected." : "Not selected."}${isSelectableOption ? " Available option." : ""}${isDisabled ? " Not available." : ""}`}
      aria-roledescription="Time slot"
      aria-disabled={isDisabled}
      data-slot-id={timeSlot.id.toString()}
      data-option-id={optionId}
      onMouseDown={isDisabled ? undefined : (e) => onMouseDown(e, timeSlot)}
      onMouseOver={isDisabled ? undefined : (e) => onMouseOver(e, timeSlot)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchStart={isDisabled ? undefined : (e) => onTouchStart(e, timeSlot)}
      onTouchMove={isDisabled ? undefined : (e) => onTouchMove(e, timeSlot)}
      onTouchEnd={isDisabled ? undefined : (e) => onTouchEnd(e, timeSlot)}
      onKeyDown={(e) => onKeyDown(e, timeSlot, dayIndex, timeIndex)}
      data-time-index={timeIndex}
      data-day-index={dayIndex}
    >
      {/* Duration badge shown only for first slot in contiguous block (freeform mode) */}
      {!isOptionsMode && isSelected && isFirstSlotInContiguousBlock && (
        <SchedulingAvailabilityTimeSlotBadge
          duration={matchingSlot?.duration}
        />
      )}
      {/* Checkmark badge shown for first slot of selected options (options mode) */}
      {isOptionsMode && isPartOfSelectedOption && isFirstSlotOfOption && (
        <SchedulingAvailabilityTimeSlotBadge showCheckmark />
      )}
    </div>
  );
}

export default SchedulingAvailabilityTimeSlot;

